import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BasicDisplayUnits } from 'hooks/useDisplayUnits/useDisplayUnits';
import { DisplayCustomUnitConversion, DisplayUnitAbbrev } from 'modules/shared/constant';
import { UnitSystem } from 'providers/api';
import React from 'react';
import { PCPipe } from '../MineModel/Pipes';

const PREFIX = 'PipeTypeSelectField';
const classes = {
  text: `${PREFIX}-text`,
};

const StyledTextField = styled(TextField)(() => ({
  [`& .${classes.text}`]: {
    flexGrow: 1,
  },
}));

interface PipeTypeSelectFieldProps {
  pipeTypes: Record<string, PCPipe>;
  value: string;
  onChange: (pipeTypeId: string | null) => void;
  label?: string;
  unitSystem: UnitSystem;
  required?: boolean;
}

const PipeTypeSelectField = ({ value, pipeTypes, onChange, label, unitSystem, required }: PipeTypeSelectFieldProps) => {
  const unitAabbrev = DisplayUnitAbbrev(unitSystem);
  const customDisplayConversion = DisplayCustomUnitConversion(unitSystem);

  const pipeArray = React.useMemo(() => Object.values(pipeTypes), [pipeTypes]);

  const [selectedPipeType, setSelectedPipeType] = React.useState<PCPipe | null>(
    pipeArray.find((pipe) => pipe.pipeId === value) ?? null,
  );

  React.useEffect(() => {
    const newSelectedPipeType = pipeArray.find((pipe) => pipe.pipeId === value) ?? null;
    setSelectedPipeType(newSelectedPipeType);
  }, [value, pipeArray]);

  React.useEffect(() => {
    onChange(selectedPipeType?.pipeId ?? null);
  }, [selectedPipeType]);

  return (
    <Autocomplete
      color="secondary"
      id="controlled-demo"
      value={selectedPipeType}
      options={pipeArray}
      getOptionLabel={(pipe: PCPipe) => pipe.name}
      onChange={(_, newValue: PCPipe | null) => setSelectedPipeType(newValue)}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li
            key={key || option.pipeId}
            {...optionProps}
            style={{
              cursor: 'pointer',
              padding: '4px 8px',
              listStyle: 'none',
            }}
          >
            <div
              style={{
                border: '1px solid #ccc',
                borderRadius: 6,
                padding: '8px 12px',
                width: '100%',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {`${option.name} - ${option.clss}`}
              </Typography>
              <Grid container>
                <Grid size={6}>
                  <Typography variant="body2">
                    <Trans id="Inside diameter">
                      Inside diameter
                      {' '}
                      (
                      {unitAabbrev[BasicDisplayUnits.Diameter]}
                      ):
                    </Trans>
                    <br />
                    <Trans id="Pressure rating">
                      Pressure rating
                      {' '}
                      (
                      {unitAabbrev[BasicDisplayUnits.PressureUCS]}
                      ):
                    </Trans>
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2">
                    {customDisplayConversion[BasicDisplayUnits.Diameter](option.insideDiameter)}
                    <br />
                    {customDisplayConversion[BasicDisplayUnits.PressureUCS](option.pressureRating)}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </li>
        );
      }}
      renderInput={(params) => (
        <StyledTextField
          color="secondary"
          {...params}
          fullWidth
          label={label || i18n._('Stope Pipe Type')}
          variant="outlined"
          required={required}
        />
      )}
    />
  );
};

export default PipeTypeSelectField;
